import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Form, Input, InputNumber, DatePicker, Button, Card, Typography, Space, Divider, message, Select, Modal } from "antd";
import { EuroOutlined, CalendarOutlined, NumberOutlined, CalculatorOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getUserDetails, isAdminUser } from "../../../services/authService";
import { getUserInfo } from "../../../services/localStorage";
import {
  createCruiseOrder,
  getCruiseOrder,
  updateCruiseOrder,
  markCruiseOrderPaidManual,
  getAgents,
} from "../../../services/fhpService";
import { prepareManoQuotePayment, extractManoApiError } from "../../../services/manoQuotesService";
import { useAppContext } from "../../../contexts/AppContext";
import { round2 } from "../../../contexts/AppContext";
import { CardsProvider } from "../../../contexts/CardsContext";
import { compactPricingAuditMeta } from "../../../pricing/compactPricingAuditMeta";
import SplitPayment from "../../../Components/Agent/PaymentAgent/SplitPayment/splitpayment";
import { Loader } from "../../../Components/Loader/Loader";
import "./CruisePayment.css";

const CRUISE_NOTE_SKIP_RE = /^(mano_quote_id\s*=|מקור:)/i;
const CRUISE_NOTE_FIELD_RE =
  /^(לקוח|טלפון|מייל|אימייל|email|תאריך הפלגה|מסלול|חדרים|מחיר|מספר הצעה)\s*[:：]/i;

function pickCruiseNoteField(notes, label) {
  const text = String(notes || "");
  const m = text.match(new RegExp(`^${label}\\s*[:：]\\s*(.+)$`, "im"));
  return (m?.[1] || "").trim();
}

function parseCruiseNotes(notes) {
  const text = String(notes || "");
  const extra = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !CRUISE_NOTE_SKIP_RE.test(line))
    .filter((line) => !CRUISE_NOTE_FIELD_RE.test(line));
  return {
    customer: pickCruiseNoteField(notes, "לקוח"),
    phone: pickCruiseNoteField(notes, "טלפון"),
    email:
      pickCruiseNoteField(notes, "מייל") ||
      pickCruiseNoteField(notes, "אימייל") ||
      pickCruiseNoteField(notes, "email"),
    route: pickCruiseNoteField(notes, "מסלול"),
    rooms: pickCruiseNoteField(notes, "חדרים"),
    extra: extra.join(" · "),
  };
}

function formatCruiseDate(value) {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD.MM.YYYY") : String(value);
}

function formatIlsAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("he-IL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ₪`;
}

function cruiseAgentDisplayName(orderData) {
  const agentObj = orderData?.agentName || {};
  return (
    [agentObj.firstName, agentObj.lastName].filter(Boolean).join(" ").trim() ||
    [orderData?.user_first_name, orderData?.user_last_name].filter(Boolean).join(" ").trim() ||
    ""
  );
}

function normalizeCruiseAgentOptions(raw) {
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.data?.agents)
        ? raw.data.agents
        : Array.isArray(raw?.agents)
          ? raw.agents
          : [];
  return list
    .map((agent) => {
      const id = agent?.id ?? agent?.agent_id ?? agent?.agentId;
      if (id == null || id === "") return null;
      const name = (
        agent?.name ||
        agent?.fullname ||
        `${agent?.firstname || ""} ${agent?.lastname || ""}`
      )
        .trim()
        .replace(/\s+/g, " ");
      const email = agent?.email || "";
      const label = name || `סוכן #${id}`;
      return {
        value: Number(id),
        label: email ? `${label} · ${email}` : label,
      };
    })
    .filter((opt) => opt && Number.isFinite(opt.value))
    .sort((a, b) => String(a.label).localeCompare(String(b.label), "he"));
}

const { Title, Text } = Typography;
const { TextArea } = Input;

const CruisePayment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlOrderId = String(searchParams.get("orderId") || "").trim() || null;
  const urlQuoteId = String(searchParams.get("quoteId") || "").trim() || null;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(Boolean(urlOrderId || urlQuoteId));
  const [orderReqData, setOrderReqData] = useState(null);
  const [orderId, setOrderId] = useState(urlOrderId);
  const { EURrate, loadEURRate, agentEntitlements } = useAppContext();
  const [calculatedValues, setCalculatedValues] = useState({
    returnDate: null,
    amountILS: 0,
    commission: 0,
  });
  const [amountDraft, setAmountDraft] = useState(null);
  const [savingAmount, setSavingAmount] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [savingAgent, setSavingAgent] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const isAdmin = isAdminUser();

  const user = getUserDetails();
  const UserInfo = getUserInfo();

  // Load EUR rate from admin settings on component mount
  useEffect(() => {
    if (!EURrate) {
      loadEURRate();
    }
  }, [EURrate, loadEURRate]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getAgents();
        if (!cancelled) {
          setAgentOptions(normalizeCruiseAgentOptions(res));
        }
      } catch {
        if (!cancelled) setAgentOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  // Calculate full price for cruise order
  const full_price = useMemo(() => {
    if (orderReqData && orderReqData.cruiseInfo?.amount_ils) {
      return parseFloat(orderReqData.cruiseInfo.amount_ils);
    }
    return calculatedValues.amountILS || 0;
  }, [orderReqData, calculatedValues.amountILS]);

  const cruisePaymentCardsAuditMeta = useMemo(
    () =>
      compactPricingAuditMeta({
        refKind: orderReqData?.id != null ? "booking" : undefined,
        bookingId: orderReqData?.id != null ? String(orderReqData.id) : undefined,
        productType:
          orderReqData?.deal_type != null
            ? String(orderReqData.deal_type)
            : undefined,
        bookingStatus:
          orderReqData?.status != null ? String(orderReqData.status) : undefined,
        agentId: UserInfo?.id != null ? String(UserInfo.id) : undefined,
        agentPlan:
          agentEntitlements?.package != null
            ? String(agentEntitlements.package)
            : undefined,
        channelType: "agent_live",
        pricingModel: "cruisePayment:CardsProvider",
      }),
    [
      orderReqData?.id,
      orderReqData?.deal_type,
      orderReqData?.status,
      UserInfo?.id,
      agentEntitlements?.package,
    ]
  );

  // Load cruise order data
  const getOrderData = () => {
    if (!orderId) return;
    
    const getOrder = async () => {
      setIsLoadingOrder(true);
      try {
        const userInfo = getUserInfo();
        const agentId = userInfo?.id || 0;

        const res = await getCruiseOrder({
          id: orderId,
          agentID: agentId,
        });

        let orderData = null;
        if (res?.data?.success && res?.data?.data) {
          orderData = res.data.data;
        } else if (res?.data?.data) {
          orderData = res.data.data;
        } else if (res?.data) {
          orderData = res.data;
        }

        if (orderData) {
          const amountIls = orderData.cruise?.amount_ils || 
                           orderData.amount_ils || 
                           orderData.packagepricedetails?.FinalPrice || 
                           0;
          const notes = orderData.cruise?.notes || orderData.notes || "";
          const parsedNotes = parseCruiseNotes(notes);
          const customerName =
            orderData.customer_name ||
            orderData.cruise?.customer_name ||
            parsedNotes.customer ||
            "";
          
          const formattedOrderData = {
            id: orderData.id,
            agent_id: orderData.agent_id,
            agent_display_name: cruiseAgentDisplayName(orderData),
            order_number: orderData.order_number,
            deal_type: orderData.deal_type || 'Cruise',
            status: orderData.status,
            fullname: customerName,
            cell: orderData.cell || parsedNotes.phone || "",
            email: orderData.email || parsedNotes.email || "",
            cruise: orderData.cruise || {
              departure_date: orderData.departure_date,
              return_date: orderData.return_date,
              nights: orderData.nights,
              amount_eur: orderData.amount_eur,
              amount_ils: amountIls,
              commission: orderData.commission,
              eur_rate: orderData.eur_rate,
              notes: orderData.notes
            },
            packagepricedetails: orderData.packagepricedetails || {
              FinalPrice: parseFloat(amountIls)
            },
            cruiseInfo: {
              departure_date: orderData.cruise?.departure_date || orderData.departure_date,
              return_date: orderData.cruise?.return_date || orderData.return_date,
              nights: orderData.cruise?.nights || orderData.nights,
              amount_eur: orderData.cruise?.amount_eur || orderData.amount_eur,
              amount_ils: parseFloat(amountIls),
              commission: orderData.cruise?.commission || orderData.commission,
              eur_rate: orderData.cruise?.eur_rate || orderData.eur_rate,
              notes: notes,
              route: parsedNotes.route,
              rooms: parsedNotes.rooms,
              extraNotes: parsedNotes.extra,
            },
            payments: orderData.payments || [],
            totalPaid: orderData.totalPaid || Number(orderData.sumpaid) || 0,
            remainingAmount: orderData.remainingAmount != null
              ? orderData.remainingAmount
              : parseFloat(amountIls),
            from_mano_quote: Boolean(orderData.from_mano_quote || urlQuoteId),
            mano_quote_id: orderData.mano_quote_id || urlQuoteId,
          };

          setOrderReqData(formattedOrderData);
          setAmountDraft(parseFloat(amountIls) || 0);
        }
      } catch (error) {
        console.error('[CruisePayment] Error loading order:', error);
        message.error("שגיאה בטעינת פרטי ההזמנה");
      } finally {
        setIsLoadingOrder(false);
      }
    };

    getOrder();
  };

  // Load order when orderId changes
  useEffect(() => {
    if (orderId) {
      getOrderData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Quote deep-link: create/ensure cruise order from Mano quote, then load payment.
  useEffect(() => {
    if (orderId || !urlQuoteId) return;
    let cancelled = false;
    (async () => {
      setIsLoadingOrder(true);
      try {
        const res = await prepareManoQuotePayment(urlQuoteId);
        const nextId =
          res?.cruise_order_id ||
          res?.crm?.cruise_order_id ||
          res?.crm?.cruise_order?.cruise_order_id;
        if (cancelled) return;
        if (!nextId) {
          message.error("לא התקבל מספר הזמנת מנו לתשלום");
          setIsLoadingOrder(false);
          return;
        }
        setOrderId(String(nextId));
      } catch (error) {
        if (!cancelled) {
          message.error(extractManoApiError(error, "פתיחת תשלום מנו מההצעה נכשלה"));
          setIsLoadingOrder(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuoteId, orderId]);

  // Calculate return date and amounts when form values change
  const handleFormChange = () => {
    const formValues = form.getFieldsValue();
    const departureDate = formValues.departureDate;
    const nights = formValues.nights;
    const amountEUR = formValues.amountEUR;

    // Calculate return date
    if (departureDate && nights && parseInt(nights) > 0) {
      const returnDate = dayjs(departureDate).add(parseInt(nights), "day");
      form.setFieldsValue({ returnDate });
      setCalculatedValues((prev) => ({ ...prev, returnDate }));
    } else {
      setCalculatedValues((prev) => ({ ...prev, returnDate: null }));
    }

    // Calculate amounts
    if (amountEUR && EURrate && parseFloat(amountEUR) > 0) {
      const amountILS = round2(parseFloat(amountEUR) * EURrate);
      const commission = round2(amountILS * 0.08); // 8% commission
      setCalculatedValues((prev) => ({
        ...prev,
        amountILS,
        commission,
      }));
    } else {
      setCalculatedValues((prev) => ({
        ...prev,
        amountILS: 0,
        commission: 0,
      }));
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Recalculate values to ensure they're up to date
      const departureDate = values.departureDate;
      const nights = parseInt(values.nights);
      const amountEUR = parseFloat(values.amountEUR);
      
      // Calculate return date if not already calculated
      let returnDate = calculatedValues.returnDate;
      if (!returnDate && departureDate && nights && nights > 0) {
        returnDate = dayjs(departureDate).add(nights, "day");
      }
      
      if (!returnDate) {
        message.error("נא לוודא שתאריך החזרה מחושב. בדוק שתאריך הפלגה ומספר הלילות תקינים.");
        setLoading(false);
        return;
      }

      // Calculate amounts if not already calculated
      let amountILS = calculatedValues.amountILS;
      let commission = calculatedValues.commission;
      
      if ((!amountILS || amountILS <= 0) && amountEUR && EURrate && amountEUR > 0) {
        amountILS = round2(amountEUR * EURrate);
        commission = round2(amountILS * 0.08);
      }

      // Validate calculated values
      if (!amountILS || amountILS <= 0) {
        message.error("נא לוודא שהסכום בשקלים מחושב. בדוק ששער האירו תקין.");
        setLoading(false);
        return;
      }

      if (!commission || commission <= 0) {
        message.error("נא לוודא שהעמלה מחושבת.");
        setLoading(false);
        return;
      }

      if (!EURrate || EURrate <= 0) {
        message.error("שגר האירו לא תקין. נא לרענן את הדף.");
        setLoading(false);
        return;
      }

      // Validate user and user.id
      if (!user || !user.id) {
        console.error('[CruisePayment] User or user.id is missing:', user);
        message.error("שגיאה: לא ניתן לזהות את המשתמש. נא להתחבר מחדש.");
        setLoading(false);
        return;
      }

      const orderData = {
        agent_id: user.id,
        order_number: values.orderNumber,
        departure_date: dayjs(departureDate).format("YYYY-MM-DD"),
        return_date: dayjs(returnDate).format("YYYY-MM-DD"),
        nights: nights,
        amount_eur: amountEUR,
        amount_ils: amountILS,
        commission: commission,
        eur_rate: EURrate,
        deal_type: "Cruise",
        notes: values.notes || "",
      };

      // Debug: Log the data being sent
      console.log('[CruisePayment] Sending order data:', orderData);
      console.log('[CruisePayment] User:', user);
      console.log('[CruisePayment] User ID:', user.id);
      console.log('[CruisePayment] Calculated values:', calculatedValues);
      console.log('[CruisePayment] API URL:', import.meta.env.VITE_API_URL);

      const response = await createCruiseOrder(orderData);
      
      console.log('[CruisePayment] Response:', response);
      console.log('[CruisePayment] Response data:', response?.data);
      console.log('[CruisePayment] Response status:', response?.status);
      
      // Check if response is null or has error status
      if (!response) {
        console.error('[CruisePayment] No response received');
        message.error("שגיאה: לא התקבלה תגובה מהשרת");
        setLoading(false);
        return;
      }
      
      if (response?.status && (response.status === 400 || response.status >= 400)) {
        const errorData = response?.data || {};
        const errorMsg = errorData.message || errorData.error?.message || "שגיאה ביצירת ההזמנה";
        const missingFields = errorData.missing_fields || errorData.required || [];
        
        console.error('[CruisePayment] Error response:', {
          status: response?.status,
          statusText: response?.statusText,
          data: errorData,
          message: errorMsg,
          missingFields: missingFields,
          fullResponse: JSON.stringify(response, null, 2),
          errorDataString: JSON.stringify(errorData, null, 2)
        });
        
        if (missingFields.length > 0) {
          message.error(`${errorMsg}: ${missingFields.join(', ')}`);
        } else {
          message.error(errorMsg);
        }
        setLoading(false);
        return;
      }
      
      // Extract order ID from response
      // Response structure: { status: 200, data: { success: true, data: { id: ... } } }
      // or: { status: 200, data: { id: ... } }
      let orderId = null;
      const responseData = response?.data;
      
      if (responseData?.success && responseData?.data?.id) {
        orderId = responseData.data.id;
      } else if (responseData?.id) {
        orderId = responseData.id;
      } else if (responseData?.success && responseData?.id) {
        orderId = responseData.id;
      }
      
      console.log('[CruisePayment] Extracted order ID:', orderId);
      
      if (orderId) {
        message.success("הזמנה נוצרה בהצלחה!");
        
        // Set order ID to load order data and show payment structure
        setOrderId(orderId);
      } else {
        const errorMsg = response?.data?.message || response?.message || "שגיאה ביצירת ההזמנה - לא התקבל מספר הזמנה";
        console.error('[CruisePayment] Error - No order ID in response:', response);
        console.error('[CruisePayment] Response structure:', {
          status: response?.status,
          data: response?.data,
          keys: response ? Object.keys(response) : []
        });
        message.error(errorMsg);
      }
    } catch (error) {
      console.error("[CruisePayment] Error creating cruise order:", error);
      console.error("[CruisePayment] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      const errorMsg = error.response?.data?.message || error.message || "שגיאה ביצירת ההזמנה";
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const amountLocked =
    Number(orderReqData?.totalPaid || 0) > 0 ||
    String(orderReqData?.status || "").toLowerCase() === "paid";
  const agentDisplayName =
    orderReqData?.agent_display_name ||
    [UserInfo?.firstname, UserInfo?.lastname].filter(Boolean).join(" ") ||
    UserInfo?.email ||
    "—";
  const agentSelectOptions = useMemo(() => {
    const opts = [...agentOptions];
    const currentId = Number(orderReqData?.agent_id);
    if (Number.isFinite(currentId) && !opts.some((opt) => Number(opt.value) === currentId)) {
      opts.unshift({
        value: currentId,
        label: orderReqData?.agent_display_name || `סוכן #${currentId}`,
      });
    }
    return opts;
  }, [agentOptions, orderReqData?.agent_id, orderReqData?.agent_display_name]);

  const handleSaveAmount = async () => {
    const next = round2(Number(amountDraft));
    if (!Number.isFinite(next) || next <= 0) {
      message.error("נא להזין סכום תקין בשקלים");
      return;
    }
    if (!orderId) {
      message.error("לא נמצאה הזמנה לעדכון");
      return;
    }
    if (amountLocked) {
      message.warning("לא ניתן לשנות סכום לאחר שבוצע תשלום");
      return;
    }
    setSavingAmount(true);
    try {
      const res = await updateCruiseOrder(orderId, {
        amount_ils: next,
        agent_id: UserInfo?.id,
      });
      if (!res?.data?.success) {
        message.error(res?.data?.message || "עדכון הסכום נכשל");
        return;
      }
      message.success("הסכום עודכן");
      getOrderData();
    } catch (error) {
      message.error(error?.response?.data?.message || "עדכון הסכום נכשל");
    } finally {
      setSavingAmount(false);
    }
  };

  const handleChangeAgent = async (nextAgentId) => {
    if (!isAdmin || !orderId) return;
    if (String(nextAgentId) === String(orderReqData?.agent_id)) return;
    setSavingAgent(true);
    try {
      const res = await updateCruiseOrder(orderId, {
        agent_id: UserInfo?.id,
        new_agent_id: nextAgentId,
      });
      if (!res?.data?.success) {
        message.error(res?.data?.message || "החלפת הסוכן נכשלה");
        return;
      }
      message.success("הסוכן עודכן");
      getOrderData();
    } catch (error) {
      message.error(error?.response?.data?.message || "החלפת הסוכן נכשלה");
    } finally {
      setSavingAgent(false);
    }
  };

  const handleMarkPaidManual = () => {
    if (!isAdmin || !orderId) return;
    if (amountLocked) {
      message.warning("ההזמנה כבר מסומנת כשולמה");
      return;
    }
    Modal.confirm({
      title: "סגירת הזמנה כשולמה ידנית",
      content: `לסמן את ${orderReqData?.order_number || orderId} כשולמה במלואה בסכום ${formatIlsAmount(full_price)}? הפעולה תתעדכן גם בדוקט.`,
      okText: "סמן כשולמה",
      cancelText: "ביטול",
      okButtonProps: { danger: true },
      onOk: async () => {
        setMarkingPaid(true);
        try {
          const res = await markCruiseOrderPaidManual(orderId, {
            agent_id: UserInfo?.id,
          });
          if (!res?.data?.success) {
            message.error(res?.data?.message || "סימון התשלום נכשל");
            return;
          }
          message.success("ההזמנה סומנה כשולמה ידנית");
          getOrderData();
        } catch (error) {
          message.error(error?.response?.data?.message || "סימון התשלום נכשל");
        } finally {
          setMarkingPaid(false);
        }
      },
    });
  };

  // If order is loaded, show payment structure
  if (orderReqData && !isLoadingOrder) {
    return (
      <CardsProvider
        totalPrice={full_price}
        pricingAuditMeta={cruisePaymentCardsAuditMeta}
      >
        <div className="cruise-payment-container" style={{ width: "100%" }}>
          <div className="payment-agent tw-w-full lg:tw-w-[85%]">
            <div className="payment-title-wrapper" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <p className="payment-title" style={{ fontSize: "24px", fontWeight: "bold" }}>סיכום הזמנת מנו ספנות</p>
              {urlQuoteId || orderReqData?.from_mano_quote ? (
                <Button
                  type="default"
                  onClick={() => navigate("/p/dashboard/mano-quotes")}
                >
                  חזרה להצעות מנו
                </Button>
              ) : (
                <Button
                  type="default"
                  onClick={() => {
                    setOrderId(null);
                    setOrderReqData(null);
                    form.resetFields();
                  }}
                >
                  יצירת הזמנה חדשה
                </Button>
              )}
            </div>

            <div className="cruise-payment-layout payment-wrapper-wrapper">
              <aside className="cruise-payment-sidebar">
                <div className="cruise-payment-sidebar-card">
                  <h3 className="cruise-payment-sidebar-title">פרטי הזמנה</h3>
                  <dl className="cruise-payment-sidebar-list">
                    <div className="cruise-payment-sidebar-row">
                      <dt>סוכן</dt>
                      <dd>
                        {isAdmin ? (
                          <Select
                            showSearch
                            optionFilterProp="label"
                            value={
                              orderReqData.agent_id != null && orderReqData.agent_id !== ""
                                ? Number(orderReqData.agent_id)
                                : undefined
                            }
                            options={agentSelectOptions}
                            onChange={handleChangeAgent}
                            loading={savingAgent}
                            disabled={savingAgent}
                            placeholder="בחר סוכן"
                            className="cruise-payment-agent-select"
                          />
                        ) : (
                          agentDisplayName
                        )}
                      </dd>
                    </div>
                    <div className="cruise-payment-sidebar-row">
                      <dt>לקוח</dt>
                      <dd>{orderReqData.fullname || "—"}</dd>
                    </div>
                    {orderReqData.cell ? (
                      <div className="cruise-payment-sidebar-row">
                        <dt>טלפון</dt>
                        <dd>{orderReqData.cell}</dd>
                      </div>
                    ) : null}
                    {orderReqData.email ? (
                      <div className="cruise-payment-sidebar-row">
                        <dt>מייל</dt>
                        <dd>{orderReqData.email}</dd>
                      </div>
                    ) : null}
                    <div className="cruise-payment-sidebar-row">
                      <dt>מספר הזמנה</dt>
                      <dd>{orderReqData.order_number || orderId}</dd>
                    </div>
                    {orderReqData.status ? (
                      <div className="cruise-payment-sidebar-row">
                        <dt>סטטוס</dt>
                        <dd>{orderReqData.status}</dd>
                      </div>
                    ) : null}
                  </dl>
                </div>

                <div className="cruise-payment-sidebar-card">
                  <h3 className="cruise-payment-sidebar-title">פרטי הפלגה</h3>
                  <dl className="cruise-payment-sidebar-list">
                    {orderReqData.cruiseInfo?.departure_date ? (
                      <div className="cruise-payment-sidebar-row">
                        <dt>יציאה</dt>
                        <dd>{formatCruiseDate(orderReqData.cruiseInfo.departure_date)}</dd>
                      </div>
                    ) : null}
                    {orderReqData.cruiseInfo?.return_date ? (
                      <div className="cruise-payment-sidebar-row">
                        <dt>חזרה</dt>
                        <dd>{formatCruiseDate(orderReqData.cruiseInfo.return_date)}</dd>
                      </div>
                    ) : null}
                    {orderReqData.cruiseInfo?.nights ? (
                      <div className="cruise-payment-sidebar-row">
                        <dt>לילות</dt>
                        <dd>{orderReqData.cruiseInfo.nights}</dd>
                      </div>
                    ) : null}
                    {orderReqData.cruiseInfo?.route ? (
                      <div className="cruise-payment-sidebar-row cruise-payment-sidebar-row-stack">
                        <dt>מסלול</dt>
                        <dd>{orderReqData.cruiseInfo.route}</dd>
                      </div>
                    ) : null}
                    {orderReqData.cruiseInfo?.rooms ? (
                      <div className="cruise-payment-sidebar-row cruise-payment-sidebar-row-stack">
                        <dt>חדרים</dt>
                        <dd>{orderReqData.cruiseInfo.rooms}</dd>
                      </div>
                    ) : null}
                    {orderReqData.cruiseInfo?.extraNotes ? (
                      <div className="cruise-payment-sidebar-row cruise-payment-sidebar-row-stack">
                        <dt>הערות</dt>
                        <dd>{orderReqData.cruiseInfo.extraNotes}</dd>
                      </div>
                    ) : null}
                  </dl>
                </div>

                <div className="cruise-payment-sidebar-card">
                  <h3 className="cruise-payment-sidebar-title">סכום לתשלום</h3>
                  <label className="cruise-payment-amount-label" htmlFor="cruise-amount-ils">
                    סכום בשקלים
                  </label>
                  <div className="cruise-payment-amount-edit">
                    <InputNumber
                      id="cruise-amount-ils"
                      className="cruise-payment-amount-input"
                      min={0.01}
                      step={1}
                      precision={2}
                      value={amountDraft}
                      disabled={amountLocked}
                      addonAfter="₪"
                      controls={false}
                      onChange={(value) => setAmountDraft(value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleSaveAmount();
                        }
                      }}
                    />
                    <Button
                      type="primary"
                      onClick={handleSaveAmount}
                      loading={savingAmount}
                      disabled={amountLocked}
                    >
                      עדכון סכום
                    </Button>
                  </div>
                  {amountLocked ? (
                    <p className="cruise-payment-amount-hint">
                      לא ניתן לשנות סכום לאחר שבוצע תשלום
                    </p>
                  ) : (
                    <p className="cruise-payment-amount-hint">
                      העמלה (8%) מתעדכנת אוטומטית לפי הסכום
                    </p>
                  )}
                  <dl className="cruise-payment-sidebar-list cruise-payment-sidebar-list-tight">
                    <div className="cruise-payment-sidebar-row">
                      <dt>עמלה (8%)</dt>
                      <dd>{formatIlsAmount(orderReqData.cruiseInfo?.commission)}</dd>
                    </div>
                    <div className="cruise-payment-sidebar-row">
                      <dt>סה״כ</dt>
                      <dd className="cruise-payment-sidebar-total">{formatIlsAmount(full_price)}</dd>
                    </div>
                    {Number(orderReqData.totalPaid) > 0 ? (
                      <div className="cruise-payment-sidebar-row">
                        <dt>שולם</dt>
                        <dd>{formatIlsAmount(orderReqData.totalPaid)}</dd>
                      </div>
                    ) : null}
                    {Number(orderReqData.remainingAmount) > 0 && Number(orderReqData.totalPaid) > 0 ? (
                      <div className="cruise-payment-sidebar-row">
                        <dt>יתרה</dt>
                        <dd className="cruise-payment-sidebar-balance">
                          {formatIlsAmount(orderReqData.remainingAmount)}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                  {isAdmin && !amountLocked ? (
                    <Button
                      danger
                      block
                      className="cruise-payment-manual-paid-btn"
                      onClick={handleMarkPaidManual}
                      loading={markingPaid}
                    >
                      סמן כשולמה ידנית
                    </Button>
                  ) : null}
                </div>
              </aside>

              {/* Payment Form */}
              <div className="cruise-payment-main payment-wrapper payment-payment-details-wrapper">
                <SplitPayment
                  isDynamicOrder={false}
                  orderId={orderId}
                  handlingFee={0}
                  orderReqData={orderReqData}
                  setOrderReqData={setOrderReqData}
                  setDynamicOrder={() => {}}
                  cardFee={0}
                  baseCommissions={0}
                  packageId={null}
                  isNewOrder={false}
                  getOrderData={getOrderData}
                  totalPrice={full_price}
                  isCruiseOrder={true}
                />
              </div>
            </div>
          </div>
        </div>
      </CardsProvider>
    );
  }

  // Show loading state
  if (isLoadingOrder) {
    return (
      <div className="cruise-payment-container" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <Loader />
      </div>
    );
  }

  // Show form for creating new order
  return (
    <div className="cruise-payment-container">
      <Card className="cruise-payment-card">
        <div className="cruise-payment-header">
          <Title level={3}>תשלום עבור הזמנת מנו ספנות</Title>
          <Text type="secondary">מלא את פרטי ההזמנה והחשבונית</Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
          className="cruise-payment-form"
        >
          <Form.Item
            name="orderNumber"
            label="מספר הזמנה"
            rules={[{ required: true, message: "נא להזין מספר הזמנה" }]}
          >
            <Input
              prefix={<NumberOutlined />}
              placeholder="הזן מספר הזמנה"
              size="large"
            />
          </Form.Item>

          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Form.Item
              name="departureDate"
              label="תאריך הפלגה"
              rules={[{ required: true, message: "נא לבחור תאריך הפלגה" }]}
            >
              <DatePicker
                prefix={<CalendarOutlined />}
                placeholder="בחר תאריך הפלגה"
                size="large"
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                disabledDate={(current) => current && current < dayjs().startOf("day")}
              />
            </Form.Item>

            <Form.Item
              name="nights"
              label="מספר לילות"
              rules={[
                { required: true, message: "נא להזין מספר לילות" },
                {
                  validator: (_, value) => {
                    const numValue = parseInt(value);
                    if (!value) {
                      return Promise.reject(new Error("נא להזין מספר לילות"));
                    }
                    if (isNaN(numValue) || numValue < 1) {
                      return Promise.reject(new Error("מספר לילות חייב להיות לפחות 1"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                prefix={<CalculatorOutlined />}
                placeholder="הזן מספר לילות"
                size="large"
                min={1}
                onChange={(e) => {
                  const value = e.target.value;
                  form.setFieldsValue({ nights: value });
                  handleFormChange();
                }}
              />
            </Form.Item>

            <Form.Item
              name="returnDate"
              label="תאריך חזרה (מחושב אוטומטית)"
            >
              <DatePicker
                disabled
                size="large"
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                value={calculatedValues.returnDate}
              />
            </Form.Item>

            <Form.Item
              name="amountEUR"
              label="סכום החשבונית (אירו)"
              rules={[
                { required: true, message: "נא להזין סכום החשבונית" },
                {
                  validator: (_, value) => {
                    const numValue = parseFloat(value);
                    if (!value) {
                      return Promise.reject(new Error("נא להזין סכום החשבונית"));
                    }
                    if (isNaN(numValue) || numValue <= 0) {
                      return Promise.reject(new Error("הסכום חייב להיות גדול מ-0"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                prefix={<EuroOutlined />}
                placeholder="הזן סכום באירו"
                size="large"
                step="0.01"
                min="0.01"
                onChange={(e) => {
                  const value = e.target.value;
                  form.setFieldsValue({ amountEUR: value });
                  handleFormChange();
                }}
              />
            </Form.Item>

            {EURrate && (
              <div className="calculation-summary">
                <Divider orientation="left">סיכום חישובים</Divider>
                <div className="calculation-row">
                  <Text strong>שער אירו:</Text>
                  <Text>{EURrate.toFixed(4)} ₪</Text>
                </div>
                {calculatedValues.amountILS > 0 && (
                  <>
                    <div className="calculation-row">
                      <Text strong>סכום בשקלים:</Text>
                      <Text>{calculatedValues.amountILS.toLocaleString()} ₪</Text>
                    </div>
                    <div className="calculation-row">
                      <Text strong>עמלה (8%):</Text>
                      <Text className="commission-amount">
                        {calculatedValues.commission.toLocaleString()} ₪
                      </Text>
                    </div>
                  </>
                )}
              </div>
            )}

            <Form.Item name="notes" label="הערות (אופציונלי)">
              <TextArea
                rows={4}
                placeholder="הערות נוספות..."
                maxLength={500}
                showCount
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                block
                disabled={!calculatedValues.amountILS || !calculatedValues.returnDate || !EURrate}
              >
                המשך לתשלום
              </Button>
            </Form.Item>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default CruisePayment;

